import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, getComicsByCategory, formatNumber } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryComics: Comic[] = slug ? getComicsByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">分类不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - 韩国漫画网`,
    description: currentCategory.description,
    url: `https://www.koreanmanhua.com/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://www.koreanmanhua.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}漫画`}
        description={currentCategory.description}
        keywords={`${currentCategory.name}漫画,免费${currentCategory.name}韩漫,${currentCategory.name}韩漫在线看,韩国漫画网`}
        canonicalUrl={`/category/${currentCategory.slug}`}
        ogType="website"
        schema={schema}
      />

      <main>
        <Navbar />

        <div className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 py-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]} />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{currentCategory.name}漫画</h1>
                  <p className="text-sm text-foreground-500 mt-0.5">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="bg-background-50 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6">
              <div className="flex gap-1 overflow-x-auto py-3 flex-wrap">
                <Link
                  to="/category/romance"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === 'romance' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      slug === cat.slug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Comics Grid */}
          <section className="py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6">
              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                  {categoryComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                        <img
                          src={comic.coverImage}
                          alt={`${comic.title} - ${currentCategory.name}韩漫`}
                          title={`${comic.title} - 免费${currentCategory.name}漫画在线看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        {comic.isHot && (
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-fire-line text-[10px]"></i>
                              </div>
                              热门
                            </span>
                          </div>
                        )}
                        {comic.isNew && (
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                              NEW
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                          <div className="flex items-center gap-3 text-xs text-background-200">
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-eye-line text-[10px]"></i>
                              </div>
                              {formatNumber(comic.views)}
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-heart-line text-[10px]"></i>
                              </div>
                              {formatNumber(comic.likes)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-accent-500 font-medium flex items-center gap-0.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </div>
                          {comic.rating}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                          {comic.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-book-2-line text-4xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                  <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
                </div>
              )}
            </div>
          </section>
        </div>

        <Footer />
      </main>
    </>
  );
}